import { createNavigationContainerRef, StackActions } from '@react-navigation/native';
import { abortAllRequests } from '@utils/abortController';
import { store } from '@reduxLocal/persistState';
import {
    disableEasyModeFlag,
    isEasyModeNavigationTarget,
    leaveEasyModeAndNavigate,
} from './screens/MidLevel/EasyModeScreen/utils/easyModeNavigation';
import { getActiveRouteName, getActiveRoutePath } from './utils/getActiveRouteName';
import { getReportedRoute } from './utils/routeReporter';

export const navigationRef = createNavigationContainerRef();

export { getActiveRouteName, getActiveRoutePath, getReportedRoute };
export { publishNavigationState, subscribeNavigationRoute } from './utils/routeReporter';
export { getScreenFileForRoute, SCREEN_FILE_MAP } from './utils/screenFileMap';

function logNavigationState(action) {
    if (!__DEV__) return;
    try {
        const root = navigationRef.isReady() ? navigationRef.getRootState() : null;
        const leaf = navigationRef.isReady() ? navigationRef.getCurrentRoute() : null;
        console.log(`[Navigation] ${action}`, {
            current: leaf?.name ?? getActiveRouteName(root),
            path: getActiveRoutePath(root),
            reported: getReportedRoute(),
        });
    } catch (error) {
        console.warn('[Navigation] logNavigationState failed', error);
    }
}

/**
 * Leaving Easy Mode always restores the footer. Outside onboarding the save/leave confirm runs
 * first; during onboarding the flag is cleared without a prompt.
 */
async function navigateLeavingEasyMode(navigateFn, name, params) {
    try {
        const state = store.getState();
        if (!state?.UI?.easyMode || isEasyModeNavigationTarget(name, params)) {
            navigateFn();
            return true;
        }
        if (state?.UI?.onboardingInProgress) {
            navigateFn();
            disableEasyModeFlag(store.dispatch);
            return true;
        }
        return await leaveEasyModeAndNavigate(navigateFn);
    } catch (error) {
        console.warn('[Navigation] Error leaving easy mode:', error);
        navigateFn();
        return true;
    }
}

export default async function navigate(name, params) {
    if (navigationRef.isReady()) {
        const navigated = await navigateLeavingEasyMode(() => {
            if (__DEV__) console.log(`[Navigation] navigate -> ${name}`, params);
            navigationRef.navigate(name, params);
            logNavigationState(`navigate -> ${name}`);
            abortAllRequests();
        }, name, params);
        if (!navigated && __DEV__) console.log(`[Navigation] navigate cancelled - easy mode leave cancelled`);
    } else if (__DEV__) {
        console.warn('[Navigation] navigate called but navigationRef not ready', name);
    }
}

export async function stack(...args) {
    if (navigationRef.isReady()) {
        const navigated = await navigateLeavingEasyMode(() => {
            if (__DEV__) console.log(`[Navigation] stack push`, args);
            navigationRef.dispatch(StackActions.push(...args));
            logNavigationState('stack push');
        }, args[0], args[1]);
        if (!navigated && __DEV__) console.log(`[Navigation] stack push cancelled - easy mode leave cancelled`);
    } else if (__DEV__) {
        console.warn('[Navigation] stack push called but navigationRef not ready', args);
    }
}

/**
 * Leaf route name from the live navigation ref (JS-side; not Appium).
 */
export function getCurrentRouteName() {
    if (!navigationRef.isReady()) return null;
    return navigationRef.getCurrentRoute()?.name
        ?? getActiveRouteName(navigationRef.getRootState());
}
